/**
 * El reporte de expedientes —el embudo— para Supervisor y Admin.
 *
 * Nació el 12-oct-2026 como «quién completó la documentación y quién no»
 * (pedido de Software). El 13-oct pasó a ser el EMBUDO (Ruben): entran todos
 * los clientes que Software nos pasó o que empezaron el flujo, no solo los que
 * llegaron al checklist, cada uno con su fase (`shared/embudo.py`), si le
 * escribió al agente y cuándo pasó por cada momento. La fase y «completó» los
 * decide el servidor; acá se arman los pedidos —el servidor pagina y filtra,
 * de a 30— y el Excel, con los mismos rótulos que la pantalla.
 *
 * Puro: lo corre `tests/unit/test_reporte_de_expedientes.py` en Node.
 */
import { telefonoDe } from './identidad'
import { libroXlsx, serialDeExcel, type Celda, type Estilo } from './xlsx'

/** Las fases, en el orden del embudo. Las mismas claves que `embudo.FASES`. */
export const FASES: { valor: string; label: string }[] = [
  { valor: 'no_escribio', label: 'No le escribió al agente' },
  { valor: 'en_preguntas', label: 'En las preguntas' },
  { valor: 'sin_documentos', label: 'Checklist sin documentos' },
  { valor: 'documentos_parciales', label: 'Documentos parciales' },
  { valor: 'completo', label: 'Entregó todo' },
]

/** Las etapas acumuladas: cuántos llegaron AL MENOS a cada una (`embudo.ETAPAS`). */
export const ETAPAS: { valor: string; label: string; fases: string[] | null }[] = [
  { valor: 'entraron', label: 'Entraron', fases: null },
  { valor: 'escribieron', label: 'Escribieron al agente',
    fases: ['en_preguntas', 'sin_documentos', 'documentos_parciales', 'completo'] },
  { valor: 'checklist', label: 'Llegaron al checklist',
    fases: ['sin_documentos', 'documentos_parciales', 'completo'] },
  { valor: 'documentos', label: 'Mandaron documentos', fases: ['documentos_parciales', 'completo'] },
  { valor: 'completaron', label: 'Entregaron todo', fases: ['completo'] },
]

export interface FilaExpediente {
  conversation_id: string
  nombre: string
  dni: string
  phone?: string | null
  contact_phone?: string | null
  celular_declarado?: string | null
  bsuid?: string | null
  /** El usuario de WhatsApp, sin @: lo único que identifica a un cliente con
   *  alias. Llega por nuestro número; Software no lo manda. */
  alias: string
  /** «software», «coe»… —el socio que nos pasó al cliente— o «directo». */
  canal: string
  /** Entrada al embudo: el pase o el primer contacto (`created_at`), UTC. */
  entrada: string
  fase: string
  fase_label: string
  escribio: boolean
  /** La pregunta filtro en la que quedó, si está en las preguntas. */
  pregunta: string
  /** Cuándo se generó el checklist del expediente vigente, o ''. */
  inicio: string
  ultima_actividad_cliente: string
  asesor: string
  derivado: boolean
  atendido_por_persona: boolean
  /** Cuándo la sesión volvió a Software, o ''. */
  devuelto_al_socio: string
  /** La PRIMERA vez que pasó por cada momento (desde el 13-oct-2026). */
  marcas: Record<string, string>
  expediente: { estado: string; label: string } | null
  alertas: string[]
  alerta_critica: boolean
  requeridos: number
  recibidos: number
  recibidos_lista: string[]
  /** Cada requisito aplicable con su nombre corto («DNI cónyuge») y su estado. */
  documentos: DocumentoDelReporte[]
  faltantes: string[]
  completo: boolean
  /** Desde cuándo está listo el expediente vigente, o '' (03-nov-2026). */
  completo_ts: string
  /** Cómo cerró: lo decide la conversación al cerrar (`shared/expediente_listo.py`). */
  como_cerro: ComoCerro
  /** Quién lo bajó y cuándo, o null si nadie todavía. */
  descarga: Descarga | null
  /** Completo, sin alertas y sin descargar. */
  por_descargar: boolean
}

/** Las claves de `shared/expediente_listo.py`; '' si no está listo. */
export type ComoCerro = '' | 'ia' | 'asesor' | 'alertas' | 'critica'

export interface Descarga {
  ts: string
  /** El correo de quien lo descargó. */
  por: string
  /** `portal` («Descargar todo») o `manual` («Ya lo bajé de Drive»). */
  origen: string
}

/** Lo que dice cada cierre, en la fila, en el panel y en el Excel. */
export const COMO_CERRO: Record<Exclude<ComoCerro, ''>, string> = {
  ia: 'Completo por IA',
  asesor: 'Completo con asesor',
  alertas: 'Con alertas',
  critica: 'Alerta crítica',
}

export interface DocumentoDelReporte {
  corto: string
  label: string
  estado: 'recibido' | 'observado' | 'pendiente'
}

/** «@keviinbravo», o '' si no lo conocemos. */
export function aliasVisible(r: Pick<FilaExpediente, 'alias'>): string {
  return r.alias ? `@${r.alias}` : ''
}

/** Una página de `GET /expedientes`: `siguiente` es el cursor de «Cargar más». */
export interface PaginaExpedientes {
  filas: FilaExpediente[]
  siguiente: string | null
  generado_at: string
}

/** `GET /expedientes?vista=conteo`: los totales del período con los filtros. */
export interface ConteoExpedientes {
  total: number
  completaron: number
  no_completaron: number
  fases: Record<string, number>
  etapas: Record<string, number>
  derivados: number
  atendidos_por_persona: number
  /** Completos sin alertas que nadie descargó (03-nov-2026). */
  por_descargar?: number
  /** Los canales del período, sin filtrar: las opciones del selector. */
  canales: string[]
}

/** `por_descargar` (03-nov-2026) es una opción más del mismo grupo de botones. */
export type FiltroCompleto = 'todos' | 'completaron' | 'no_completaron' | 'por_descargar'

export interface Filtros {
  /** Días de Lima, «aaaa-mm-dd»; '' no acota. Filtran por la ENTRADA. */
  desde: string
  hasta: string
  completo: FiltroCompleto
  /** '' es todas las fases. */
  fase: string
  /** '' es todos los canales. */
  canal: string
  texto: string
}

/** Filas por página en pantalla y por pedido al armar el Excel. */
export const FILAS_POR_PAGINA = 30
export const FILAS_POR_PEDIDO_EXCEL = 500
/** Lo más que trae el Excel; si el período tiene más, la hoja Resumen lo dice. */
export const TOPE_EXCEL = 5000

const CANALES: Record<string, string> = {
  software: 'Software',
  coe: 'Número del COE (prueba)',
  coe_waba: 'Número del COE (prueba)',
  directo: 'Directo',
}

export function canalLegible(canal: string): string {
  return CANALES[canal] ?? (canal ? canal.charAt(0).toUpperCase() + canal.slice(1) : '—')
}

export function faseLegible(fase: string): string {
  return FASES.find((f) => f.valor === fase)?.label ?? (fase || '—')
}

/**
 * Los parámetros de `GET /expedientes` para estos filtros. Los filtros van al
 * servidor: filtrar en el navegador una página de 30 daría resultados
 * incompletos. Lo vacío no viaja.
 */
export function parametrosDelPedido(
  f: Filtros,
  extra: { limite?: number; cursor?: string | null; vista?: 'conteo' } = {},
): Record<string, string> {
  const { desde, hasta } = limitesDeLima(f.desde, f.hasta)
  const p: Record<string, string> = {}
  if (desde) p.desde = desde
  if (hasta) p.hasta = hasta
  if (f.canal) p.canal = f.canal
  if (f.completo !== 'todos') p.completo = f.completo
  if (f.fase) p.fase = f.fase
  if (f.texto.trim()) p.texto = f.texto.trim()
  if (extra.vista) p.vista = extra.vista
  if (extra.limite) p.limite = String(extra.limite)
  if (extra.cursor) p.cursor = extra.cursor
  return p
}

/** ¿Se muestra «Cargar más»? Hay cursor y todavía no se cargó el total. */
export function quedanMas(cargadas: number, total: number | null, siguiente: string | null): boolean {
  return Boolean(siguiente) && (total === null || cargadas < total)
}

/** Las etapas acumuladas de unas filas: lo mismo que `embudo.etapas` del servidor. */
export function etapasDe(filas: FilaExpediente[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const e of ETAPAS) {
    out[e.valor] = e.fases === null ? filas.length
      : filas.filter((r) => (e.fases as string[]).includes(r.fase)).length
  }
  return out
}

/** «45 %» de `parte` sobre `total`; '' sin total. */
export function porcentaje(parte: number, total: number): string {
  return total ? `${Math.round((parte / total) * 100)} %` : ''
}

/** Perú no tiene horario de verano: UTC-5 todo el año. */
const OFFSET_LIMA_MS = 5 * 60 * 60 * 1000

/** «12/10/2026 10:28», en hora de Lima. '' si no hay fecha. */
export function fechaLima(ts: string | null | undefined): string {
  const ms = Date.parse(ts || '')
  if (Number.isNaN(ms)) return ''
  const iso = new Date(ms - OFFSET_LIMA_MS).toISOString()
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}`
}

/**
 * Los días de Lima elegidos, como lo pide el servidor: `desde` es las 00:00 de
 * Lima del primer día y `hasta` las 00:00 del día SIGUIENTE al último (se
 * compara con `<`), los dos en UTC. Una fecha vacía no acota.
 */
export function limitesDeLima(desde: string, hasta: string): { desde: string; hasta: string } {
  const inicio = (dia: string) => new Date(Date.parse(`${dia}T00:00:00Z`) + OFFSET_LIMA_MS)
  return {
    desde: desde ? inicio(desde).toISOString() : '',
    hasta: hasta ? new Date(inicio(hasta).getTime() + 24 * 60 * 60 * 1000).toISOString() : '',
  }
}

/** «aaaa-mm-dd» de hoy en Lima, menos `dias`. Lo usan los filtros por defecto. */
export function diaDeLima(ahoraMs: number, dias = 0): string {
  return new Date(ahoraMs - OFFSET_LIMA_MS - dias * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** «3 de 5», o '' si todavía no tiene checklist. */
export function documentos(r: FilaExpediente): string {
  return r.requeridos ? `${r.recibidos} de ${r.requeridos}` : ''
}

// ── Después del cierre: cómo cerró, por día y la descarga (03-nov-2026) ─────
//
// Pedido de Leslie: que el asesor vea los que el agente completó solo, sabiendo
// que se completaron, y los descargue; y que el supervisor no tenga que abrir
// uno por uno. La tabla quedó sin botones: la fila dice en una línea cómo
// cerró y en qué paso está, y el resto vive en el panel del expediente.

/** «13:24», en hora de Lima. '' si no hay fecha. */
export function horaLima(ts: string | null | undefined): string {
  return fechaLima(ts).slice(11)
}

/** «lperez» de «lperez@prestamype.com». */
export function usuarioDe(correo: string | null | undefined): string {
  return (correo || '').split('@')[0]
}

/** «sofia.castro · 09:14». */
export function quienDescargo(d: Descarga): string {
  return `${usuarioDe(d.por)} · ${horaLima(d.ts)}`
}

export type TonoDeLinea = 'brand' | 'advisor' | 'warn' | 'danger' | 'muted'

/**
 * La línea debajo de la fase de un expediente completo: cómo cerró y en qué
 * paso está —«Por IA · por descargar», «Con asesor · descargado 09:14»,
 * «Alerta crítica · revisar»—. null si no está listo: ahí la línea es la
 * pregunta o las alertas, como siempre.
 */
export function lineaDeCierre(r: Pick<FilaExpediente, 'como_cerro' | 'descarga' | 'alertas'>):
  { texto: string; tono: TonoDeLinea } | null {
  if (!r.como_cerro) return null
  if (r.como_cerro === 'critica') return { texto: 'Alerta crítica · revisar', tono: 'danger' }
  if (r.como_cerro === 'alertas') {
    const n = r.alertas.length
    return { texto: `${n || 'Con'} alerta${n === 1 ? '' : 's'} · revisar`, tono: 'warn' }
  }
  const quien = r.como_cerro === 'ia' ? 'Por IA' : 'Con asesor'
  if (r.descarga) return { texto: `${quien} · descargado ${horaLima(r.descarga.ts)}`, tono: 'muted' }
  return { texto: `${quien} · por descargar`, tono: r.como_cerro === 'ia' ? 'brand' : 'advisor' }
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'setiembre', 'octubre', 'noviembre', 'diciembre']

/** «aaaa-mm-dd» del día de Lima de un `ts` UTC, o ''. */
export function diaDeLimaDe(ts: string | null | undefined): string {
  const ms = Date.parse(ts || '')
  return Number.isNaN(ms) ? '' : diaDeLima(ms)
}

/** «Hoy · martes 3 de noviembre», «Ayer · lunes 2 de noviembre» o «domingo 1
 *  de noviembre»: el encabezado de cada día, en hora de Lima. */
export function tituloDelDia(dia: string, ahoraMs: number): string {
  if (!dia) return 'Sin fecha'
  const fecha = new Date(`${dia}T12:00:00Z`)
  const texto = `${DIAS[fecha.getUTCDay()]} ${fecha.getUTCDate()} de ${MESES[fecha.getUTCMonth()]}`
  if (dia === diaDeLima(ahoraMs)) return `Hoy · ${texto}`
  if (dia === diaDeLima(ahoraMs, 1)) return `Ayer · ${texto}`
  return texto
}

export interface GrupoDelDia<T> {
  dia: string
  titulo: string
  filas: T[]
}

/**
 * Las filas en grupos por día de Lima, en el orden en que vienen (el servidor
 * ya las ordena del más reciente al más antiguo). Un día que vuelve a aparecer
 * más abajo abre otro grupo: no se reordena nada acá.
 */
export function agruparPorDia<T>(filas: ReadonlyArray<T>, fecha: (f: T) => string,
                                 ahoraMs: number): GrupoDelDia<T>[] {
  const grupos: GrupoDelDia<T>[] = []
  for (const f of filas) {
    const dia = diaDeLimaDe(fecha(f))
    const ultimo = grupos[grupos.length - 1]
    if (ultimo && ultimo.dia === dia) ultimo.filas.push(f)
    else grupos.push({ dia, titulo: tituloDelDia(dia, ahoraMs), filas: [f] })
  }
  return grupos
}

/**
 * Dónde se abre el chat completo desde el panel del expediente. El asesor lo
 * abre en su bandeja, donde puede contestar; supervisión y admin en el
 * detalle de Conversaciones, de solo lectura, desde donde se interviene como
 * hoy. Leslie, 03-nov-2026: el chat completo lo ven los tres roles.
 */
export function rutaDelChat(conversationId: string, modo: 'asesor' | 'supervision'): string {
  const id = encodeURIComponent(conversationId)
  return modo === 'asesor' ? `/inbox?c=${id}` : `/conversations/${id}`
}

// ── Mis expedientes ────────────────────────────────────────────────────────

export type PestanaMisExpedientes = 'por_descargar' | 'en_curso' | 'con_alertas' | 'descargados'

// El rótulo primero: `{ valor, label }` es la forma de las FASES del embudo, que
// un test lee de esta fuente para cruzarlas con `embudo.FASES`.
export const PESTANAS_MIS_EXPEDIENTES: { label: string; valor: PestanaMisExpedientes }[] = [
  { label: 'Por descargar', valor: 'por_descargar' },
  { label: 'En curso', valor: 'en_curso' },
  { label: 'Con alertas', valor: 'con_alertas' },
  { label: 'Descargados', valor: 'descargados' },
]

/** `GET /mis-expedientes`: la pestaña pedida y cuántos hay en cada una. */
export interface MisExpedientes {
  pestana: PestanaMisExpedientes
  filas: FilaExpediente[]
  conteos: Record<PestanaMisExpedientes, number>
  generado_at: string
}

/** Lo que ordena y agrupa cada pestaña: la hora de listo, o la entrada en
 *  «En curso». Igual que `_fecha_de_la_pestana` del servidor. */
export function fechaDeLaPestana(r: FilaExpediente, pestana: PestanaMisExpedientes): string {
  return pestana === 'en_curso' ? r.entrada : r.completo_ts
}

/** Los parámetros de `GET /mis-expedientes`. Las fechas, como el reporte. */
export function parametrosDeMisExpedientes(pestana: PestanaMisExpedientes, desde: string,
                                           hasta: string, texto: string): Record<string, string> {
  const lim = limitesDeLima(desde, hasta)
  const p: Record<string, string> = { pestana }
  if (lim.desde) p.desde = lim.desde
  if (lim.hasta) p.hasta = lim.hasta
  if (texto.trim()) p.texto = texto.trim()
  return p
}

/**
 * El teléfono para la planilla: solo el número, siempre igual escrito.
 *
 * En pantalla `telefonoVisible` explica por qué no hay número («cuenta con
 * alias», «del precalificador»); en una columna de Excel eso mezcla números
 * con frases y no se puede filtrar ni copiar. Acá va el celular —el de su
 * WhatsApp o, si no, el que confirmó en el precalificador—, sin el 51 de país,
 * o vacío.
 */
export function telefonoDelReporte(r: FilaExpediente): string {
  const t = (telefonoDe(r) || (r.celular_declarado ?? '')).replace(/[\s+()-]/g, '')
  const peru = /^51(9\d{8})$/.exec(t)
  return peru ? peru[1] : t
}

export interface ContextoDelReporte {
  /** Los días de Lima elegidos, «aaaa-mm-dd»; '' es sin límite. */
  desde: string
  hasta: string
  /** '' es todos los canales. */
  canal: string
  completo: FiltroCompleto
  fase: string
  texto: string
  generadoMs: number
  /** Los clientes del período con los filtros; si el archivo trae menos (el
   *  tope), la hoja Resumen lo avisa. */
  totalDelPeriodo?: number
}

export const COLUMNAS_EXCEL: { titulo: string; ancho: number }[] = [
  { titulo: 'Cliente', ancho: 38 },
  { titulo: 'DNI', ancho: 12 },
  { titulo: 'Teléfono', ancho: 13 },
  { titulo: 'Usuario de WhatsApp', ancho: 20 },
  { titulo: 'Canal', ancho: 24 },
  { titulo: 'Entrada', ancho: 18 },
  { titulo: 'Fase', ancho: 26 },
  { titulo: 'Escribió al agente', ancho: 11 },
  { titulo: 'Quedó en la pregunta', ancho: 26 },
  { titulo: 'Completó', ancho: 11 },
  { titulo: 'Documentos recibidos', ancho: 13 },
  { titulo: 'Recibió', ancho: 42 },
  { titulo: 'Faltan', ancho: 42 },
  { titulo: 'Estado del expediente', ancho: 36 },
  { titulo: 'Alertas', ancho: 56 },
  { titulo: 'Alerta crítica', ancho: 11 },
  { titulo: 'Derivado a asesor', ancho: 11 },
  { titulo: 'Lo atendió una persona', ancho: 12 },
  { titulo: 'Asesor', ancho: 30 },
  { titulo: 'Primera respuesta', ancho: 18 },
  { titulo: 'Checklist', ancho: 18 },
  { titulo: 'Primer documento', ancho: 18 },
  { titulo: 'Entregó todo', ancho: 18 },
  { titulo: 'Devuelto a Software', ancho: 18 },
  { titulo: 'Última actividad del cliente', ancho: 18 },
  { titulo: 'ID de conversación', ancho: 38 },
  // 03-nov-2026: al FINAL, para que quien ya consume la planilla (BA) no vea
  // ninguna columna moverse de lugar.
  { titulo: 'Cómo cerró', ancho: 22 },
  { titulo: 'Descargado por', ancho: 30 },
  { titulo: 'Descargado a las', ancho: 18 },
]

const FILTRO_COMPLETO: Record<FiltroCompleto, string> = {
  todos: 'Todos', completaron: 'Completaron', no_completaron: 'No completaron',
  por_descargar: 'Por descargar',
}

/** La fecha de Lima de un `ts` UTC, como número de Excel; `null` sin fecha. */
function fechaExcel(ts: string | null | undefined): number | null {
  const ms = Date.parse(ts || '')
  return Number.isNaN(ms) ? null : serialDeExcel(ms, OFFSET_LIMA_MS)
}

function diaLegible(dia: string): string {
  return dia ? `${dia.slice(8, 10)}/${dia.slice(5, 7)}/${dia.slice(0, 4)}` : ''
}

function periodo(desde: string, hasta: string): string {
  if (desde && hasta) return `Del ${diaLegible(desde)} al ${diaLegible(hasta)}`
  if (desde) return `Desde el ${diaLegible(desde)}`
  if (hasta) return `Hasta el ${diaLegible(hasta)}`
  return 'Sin límite de fechas'
}

const ESTILO_DE_FASE: Record<string, Estilo> = {
  completo: 'bien', no_escribio: 'mal',
}

function siNo(valor: boolean, siEsBueno = true): Celda {
  if (valor) return { v: 'Sí', estilo: siEsBueno ? 'bien' : 'mal' }
  return { v: 'No', estilo: 'centrado' }
}

function filaDeExcel(r: FilaExpediente): Celda[] {
  const marca = (m: string) => fechaExcel(r.marcas?.[m])
  return [
    r.nombre ? { v: r.nombre, estilo: 'textoLargo' } : { v: 'Sin identificar', estilo: 'tenue' },
    { v: r.dni || null, estilo: 'centrado' },
    { v: telefonoDelReporte(r) || null, estilo: 'centrado' },
    { v: aliasVisible(r) || null, estilo: 'texto' },
    { v: canalLegible(r.canal), estilo: 'texto' },
    { v: fechaExcel(r.entrada), estilo: 'fecha' },
    { v: r.fase_label || faseLegible(r.fase), estilo: ESTILO_DE_FASE[r.fase] ?? 'pendiente' },
    r.escribio ? { v: 'Sí', estilo: 'centrado' } : { v: 'No', estilo: 'mal' },
    { v: r.pregunta || null, estilo: 'texto' },
    r.completo ? { v: 'Sí', estilo: 'bien' } : { v: 'No', estilo: 'pendiente' },
    { v: documentos(r) || null, estilo: 'centrado' },
    // Uno por renglón: separados por « | » no se leían en una celda angosta.
    { v: r.recibidos_lista.join('\n') || null, estilo: 'textoLargo' },
    { v: r.faltantes.join('\n') || null, estilo: 'textoLargo' },
    { v: r.expediente?.label || null, estilo: 'textoLargo' },
    { v: r.alertas.join('\n') || null, estilo: 'textoLargo' },
    siNo(r.alerta_critica, false),
    { v: r.derivado ? 'Sí' : 'No', estilo: 'centrado' },
    { v: r.atendido_por_persona ? 'Sí' : 'No', estilo: 'centrado' },
    r.asesor ? { v: r.asesor, estilo: 'texto' } : { v: 'Sin asignar', estilo: 'tenue' },
    { v: marca('primera_respuesta'), estilo: 'fecha' },
    { v: fechaExcel(r.inicio) ?? marca('checklist'), estilo: 'fecha' },
    { v: marca('primer_documento'), estilo: 'fecha' },
    { v: marca('completo'), estilo: 'fecha' },
    { v: fechaExcel(r.devuelto_al_socio), estilo: 'fecha' },
    { v: fechaExcel(r.ultima_actividad_cliente), estilo: 'fecha' },
    { v: r.conversation_id, estilo: 'tenue' },
    { v: r.como_cerro ? COMO_CERRO[r.como_cerro] : null, estilo: 'texto' },
    { v: r.descarga?.por || null, estilo: 'texto' },
    { v: fechaExcel(r.descarga?.ts), estilo: 'fecha' },
  ]
}

/**
 * El Excel de las filas filtradas: la hoja «Expedientes», con una fila por
 * cliente, y la hoja «Resumen» con el período, los filtros y el embudo — una
 * planilla que circula sin decir de qué período es se lee como si fuera todo.
 */
export function aExcel(filas: FilaExpediente[], ctx: ContextoDelReporte): Uint8Array<ArrayBuffer> {
  const etapas = etapasDe(filas)
  const total = filas.length
  const rotulo = (texto: string): Celda => ({ v: texto, estilo: 'rotulo' })
  const resumen: Celda[][] = [
    [{ v: 'Reporte de expedientes', estilo: 'titulo' }],
    [],
    [rotulo('Generado'), { v: serialDeExcel(ctx.generadoMs, OFFSET_LIMA_MS), estilo: 'fechaValor' }],
    [rotulo('Período (entrada del cliente)'), { v: periodo(ctx.desde, ctx.hasta), estilo: 'valor' }],
    [rotulo('Canal'), { v: ctx.canal ? canalLegible(ctx.canal) : 'Todos', estilo: 'valor' }],
    [rotulo('Fase'), { v: ctx.fase ? faseLegible(ctx.fase) : 'Todas', estilo: 'valor' }],
    [rotulo('Completó'), { v: FILTRO_COMPLETO[ctx.completo], estilo: 'valor' }],
    [rotulo('Búsqueda'), { v: ctx.texto.trim() || '—', estilo: 'valor' }],
    [],
    [{ v: 'Embudo', estilo: 'titulo' }],
    ...ETAPAS.map((e): Celda[] => [
      rotulo(e.label), { v: etapas[e.valor], estilo: 'entero' },
      { v: total ? etapas[e.valor] / total : null, estilo: 'porcentaje' },
    ]),
    [rotulo('Derivados a un asesor'), { v: filas.filter((r) => r.derivado).length, estilo: 'entero' }],
    [rotulo('Atendidos por una persona'),
     { v: filas.filter((r) => r.atendido_por_persona).length, estilo: 'entero' }],
    ...(ctx.totalDelPeriodo !== undefined && ctx.totalDelPeriodo > filas.length
      ? [[rotulo('Atención'), {
        v: `El período tiene ${ctx.totalDelPeriodo} clientes; este archivo trae los `
          + `${filas.length} más recientes. Acota las fechas para descargar el resto.`,
        estilo: 'valor',
      }] as Celda[]]
      : []),
    [],
    // Un renglón por frase: una nota más ancha que las columnas se imprime en
    // una página propia.
    [{ v: 'Cada etapa cuenta a quienes llegaron AL MENOS hasta ahí; el porcentaje es sobre los que entraron.',
       estilo: 'normal' }],
    [{ v: '«Completó» es haber entregado todo lo que pide su checklist, aunque tenga alertas.',
       estilo: 'normal' }],
    [{ v: 'Las fechas de cada momento existen desde el 13-oct-2026. Están en hora de Lima.',
       estilo: 'normal' }],
  ]
  return libroXlsx([
    {
      nombre: 'Expedientes',
      anchos: COLUMNAS_EXCEL.map((col) => col.ancho),
      encabezadoFijo: true,
      imprimirAlAncho: true,
      filas: [
        COLUMNAS_EXCEL.map((col): Celda => ({ v: col.titulo, estilo: 'encabezado' })),
        ...filas.map(filaDeExcel),
      ],
    },
    { nombre: 'Resumen', anchos: [34, 40, 12], filas: resumen, imprimirAlAncho: true },
  ])
}

export function nombreDelArchivo(ctx: ContextoDelReporte): string {
  return `expedientes_${ctx.desde || 'inicio'}_${ctx.hasta || 'hoy'}.xlsx`
}
